import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CustomerBaseData, IBackendResponse, UserDataModel, UserMessage } from '@zero/shared/util-models';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { environment } from 'environments/environment';
import { Observable, Subject, firstValueFrom, from as fromPromise, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CustomAmplifyService } from '../amplify/custom-amplify.service';
import { AuthService } from '../authentication/auth.service';
import { UserMessagesHolderService } from '../user-messages/user-messages-holder.service';

@Injectable({
  providedIn: 'root',
})
export class UserDataService {
  readonly #API_CONTACTDATA = '/api/user/contactdata';

  public contactData: UserDataModel;
  #contactData$ = new Subject<UserDataModel>();
  #reloadContactDataUnderway = false;

  #contactDataLastUpdate: Date;

  readonly #http = inject(HttpClient);
  readonly #amplifyService = inject(CustomAmplifyService);
  readonly #authService = inject(AuthService);
  readonly #messageService = inject(UserMessagesHolderService);

  public loadContactData$(forceReload: boolean): Subject<UserDataModel> {
    this.#reloadContactData(forceReload);
    return this.#contactData$;
  }

  #reloadContactData(forceReload: boolean) {
    if (this.#reloadContactDataUnderway) {
      return;
    }
    if (forceReload || !this.#contactDataLastUpdate || this.#contactDataLastUpdate.getTime() < new Date().getTime() - 1000) {
      this.#reloadContactDataUnderway = true;
      const url = environment.apihost + this.#API_CONTACTDATA;
      firstValueFrom(this.#http.get<UserDataModel>(url))
        .then(
          (res) => {
            // Success
            if (res) {
              this.contactData = res;
              this.#contactDataLastUpdate = new Date();
              this.#contactData$.next(res);
            }
            this.#reloadContactDataUnderway = false;
          },
          () => {
            // Error
            this.#contactData$.next(undefined); // calling .error() on a subject would terminate the subject ultimately
            this.#reloadContactDataUnderway = false;
          },
        )
        .catch(() => {
          this.#contactData$.next(undefined); // calling .error() on a subject would terminate the subject ultimately
        });
    }
  }

  public saveContactData(changedValues: UserDataModel): Observable<IBackendResponse<UserDataModel>> {
    return this.#http.post<UserDataModel>(environment.apihost + this.#API_CONTACTDATA, changedValues).pipe(
      map((result: UserDataModel) => ({
        success: true,
        data: result,
      })),
      catchError(() =>
        of({
          success: false,
          message: 'Deine Daten wurden aufgrund eines Fehlers nicht gespeichert.',
        }),
      ),
    );
  }

  public initVerificationIfNecessary(savedUser: UserDataModel): void {
    if (savedUser.emailVerified !== 'true') {
      this.#amplifyService
        .auth()
        .verifyCurrentUserAttribute('email')
        .then(() => {
          this.#messageService.addInfoMessage('Ein Verifikationscode wurde an Deine neue E-Mail-Adresse versandt.', true);
        });
    }

    if (savedUser.mobilenrVerified !== 'true') {
      this.#amplifyService
        .auth()
        .verifyCurrentUserAttribute('phone_number')
        .then(() => {
          this.#messageService.addInfoMessage('Ein Verifikationscode wurde an Deine neue Handy-Nr. versandt.', true);
        });
    }
  }

  public getPreferredUsername(): Observable<IBackendResponse<string>> {
    return this.#authService.getAuthToken().pipe(
      switchMap((token) => {
        if (!token) {
          return of(null);
        }

        return fromPromise(this.#amplifyService.auth().currentUserInfo()).pipe(
          map((userInfo) => {
            const preferredUserName = userInfo['attributes'].preferred_username;
            return {
              success: true,
              data: preferredUserName || '',
            } as IBackendResponse<string>;
          }),
          catchError(() => {
            this.#messageService.addMessage(new UserMessage('Fehler beim Laden des Login-Alias', 'danger', true));
            return of({
              success: false,
              data: '',
            } as IBackendResponse<string>);
          }),
        );
      }),
    );
  }

  public updatePreferredUsername(newUsername: string): Observable<IBackendResponse<any>> {
    return fromPromise(this.#amplifyService.auth().currentAuthenticatedUser()).pipe(
      switchMap((user: CognitoUser) =>
        fromPromise(
          this.#amplifyService.auth().updateUserAttributes(user, {
            preferred_username: newUsername,
          }),
        ).pipe(
          map(
            () =>
              ({
                success: true,
                message: `Dein Login-Alias wurde ${newUsername === '' ? 'gelöscht.' : 'gespeichert.'}`,
              }) as IBackendResponse<any>,
          ),
        ),
      ),
      catchError(() =>
        of({
          success: false,
        } as IBackendResponse<any>),
      ),
    );
  }

  public getInitialTotpCode(): Observable<IBackendResponse<{ str4QRCode: string; plainCode: string }>> {
    return fromPromise(this.#amplifyService.auth().currentAuthenticatedUser()).pipe(
      switchMap((user: CognitoUser) =>
        fromPromise(this.#amplifyService.auth().currentUserInfo()).pipe(
          switchMap((userInfo: any) =>
            fromPromise(this.#amplifyService.auth().setupTOTP(user)).pipe(
              map((response) => {
                const usernametoShow = userInfo.attributes.preferred_username ? `${userInfo.attributes.preferred_username}:` : '';
                const email = userInfo.attributes.email;
                const str = encodeURI(`otpauth://totp/${usernametoShow}${email}?secret=${response}&issuer=` + `GB-Test`);

                return {
                  success: true,
                  data: { str4QRCode: str, plainCode: response },
                } as IBackendResponse<{ str4QRCode: string; plainCode: string }>;
              }),
            ),
          ),
        ),
      ),
    );
  }

  public confirmInitialTotpCode(code: string): Observable<IBackendResponse<any>> {
    return fromPromise(this.#amplifyService.auth().currentAuthenticatedUser()).pipe(
      switchMap((user: CognitoUser) =>
        fromPromise(this.#amplifyService.auth().verifyTotpToken(user, code)).pipe(
          switchMap(() =>
            fromPromise(this.#amplifyService.auth().setPreferredMFA(user, 'TOTP')).pipe(
              map(
                (setPreferredMFAResult) =>
                  ({
                    success: true,
                    data: setPreferredMFAResult,
                  }) as IBackendResponse<any>,
              ),
            ),
          ),
        ),
      ),
    );
  }

  public deactivateTotp(): Observable<IBackendResponse<any>> {
    return fromPromise(this.#amplifyService.auth().currentAuthenticatedUser()).pipe(
      switchMap((user: CognitoUser) =>
        fromPromise(this.#amplifyService.auth().setPreferredMFA(user, 'NOMFA')).pipe(
          map((result) => ({
            success: true,
            data: result,
          })),
        ),
      ),
    );
  }

  public verifyAttributeConfirm(attributeName: string, enteredCode: string): Observable<IBackendResponse<any>> {
    return fromPromise(this.#amplifyService.auth().verifyCurrentUserAttributeSubmit(attributeName, enteredCode)).pipe(
      map((result) => {
        if (result === 'SUCCESS') {
          return {
            success: true,
            message: 'Die Verifikation war erfolgreich.',
          } as IBackendResponse<any>;
        } else {
          return {
            success: false,
            message: 'Die Verifikation ist fehlgeschlagen.',
          } as IBackendResponse<any>;
        }
      }),
      catchError((err) => {
        const retVal: IBackendResponse<any> = { success: false };
        if (err.code && err.code === 'CodeMismatchException') {
          retVal.message = 'Der eingegebene Code war falsch.';
        } else {
          retVal.message = 'Die Verifikation ist fehlgeschlagen.';
        }

        return of(retVal);
      }),
    );
  }

  public verifyAttribute(attributeName: string): Observable<IBackendResponse<any>> {
    return fromPromise(this.#amplifyService.auth().verifyCurrentUserAttribute(attributeName)).pipe(
      map(() => ({
        success: true,
        message: 'Ein Verifikationscode wurde erneut versendet.',
      })),
      catchError(() =>
        of({
          success: false,
          message: 'Fehler beim Versand des Verifikationscodes.',
        }),
      ),
    );
  }

  public approveContracts = (customerId: string): Observable<CustomerBaseData> =>
    new Observable<CustomerBaseData>((observer) => {
      this.#http.post(`${environment.apihost}/api/ext/v1/user/confirmfz`, Number(customerId)).subscribe(
        (response: CustomerBaseData) => {
          observer.next(response);
          observer.complete();
        },
        (e) => observer.error(e),
      );
    });
}
